import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import QuestionCardNew from './QuestionCardNew';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { trackQuizCompleted, trackLessonCompleted } from '../../../lib/analytics';
import { getVideoLessonProgress } from '../../../lib/videoLessonApi';
import { getTrialProgress, saveTrialProgress } from '../../../lib/trialLessonProgress';
import css from './QuizDesign/quizDesign.module.css';


const QuestionCardExample = ({ onActivityComplete, questions, lessonId: propLessonId, videoLessonId: propVideoLessonId, savedState, onStateChange }) => {
    const location = useLocation();
    const isTrialLesson = location.pathname === '/trial-lesson';
    const activeLessonId = propLessonId;
    const activeVideoLessonId = propVideoLessonId || activeLessonId;
    const { t } = useTranslation();

    const [finalScore, setFinalScore] = React.useState(0);
    
    // Load trial progress if available
    const trialQuizProgress = isTrialLesson ? getTrialProgress('quiz') : null;
    const initialSavedState = savedState || trialQuizProgress;
    
    // Якщо є savedState — тест вже починався, пропускаємо intro
    const [showIntro, setShowIntro] = useState(!initialSavedState);
    
    // Create a wrapper for onStateChange that also saves to localStorage
    const handleStateChange = React.useCallback((state) => {
        if (onStateChange) {
            onStateChange(state);
        }
        if (isTrialLesson) {
            saveTrialProgress('quiz', state);
        }
    }, [onStateChange, isTrialLesson]);

    const handleTestComplete = async (results) => { /* без змін */ };

    const handleStartTest = () => {
        setShowIntro(false);
        // Повідомити батька що intro пройдено (щоб при поверненні не показувати знову)
        handleStateChange({ ...initialSavedState, introShown: true });
    };

     if (showIntro) {
        return (
            <div className={css.quizWrap}>
                <div className={css.introScreen}>
                    {/* Icon */}
                    <div className={css.introIcon}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <path d="M12 17h.01"/>
                        </svg>
                    </div>
                    {/* Title & Description */}
                    <h2 className={css.introTitle}>{t("Check your understanding")}</h2>
                    <p className={css.introDesc}>
                        {t("Take a short 10-question quiz to check what you remember from the video.")}
                    </p>
                    {/* Stats */}
                    <div className={css.introStats}>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                            {t("10 questions")}
                        </div>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                            {t("3 question types")}
                        </div>
                        <div className={css.introStat}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {t("3–5 minutes")}
                        </div>
                    </div>
                    {/* Test Types */}
                    <div className={css.introTypesLabel}>{t("What the quiz includes")}</div>
                    <div className={css.introTypes}>
                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeWM}`}>{t("Word meaning")}</span>
                            <p>{t("Choose the meaning of a word or phrase.")}</p>
                            <div className={css.introExample}>{t('"from" means "from" or "because"')}</div>
                        </div>
                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeFB}`}>{t("Fill in the blank")}</span>
                            <p>{t("Fill in the missing word.")}</p>
                            <div className={css.introExample}>{t('"My ___ is Anna."')}</div>
                        </div>
                        <div className={css.introType}>
                            <span className={`${css.introTypeBadge} ${css.badgeL}`}>{t("Build the phrase")}</span>
                            <p>{t("Build the English phrase from the given words.")}</p>
                            <div className={css.introExample}>{t('"Check, please" → Check / please / Card')}</div>
                        </div>
                    </div>
                    {/* Start Button */}
                    <button className={css.introBtn} onClick={handleStartTest}>
                        {t("Start quiz")} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                    </button>
                    <div className={css.introNote}>{t("The quiz includes several question types and will take a few minutes.")}</div>
                </div>
            </div>
        );
    }
    
    return (
        <div>
            {activeVideoLessonId ? (
                <QuestionCardNew
                    videoLessonId={activeVideoLessonId}
                    initialQuestions={questions}
                    onTestComplete={handleTestComplete}
                    onActivityComplete={(score) => {
                        setFinalScore(score);
                        if (onActivityComplete) onActivityComplete(score);
                    }}
                    maxAttempts={2}
                    enabledTestTypes={{ yesNo: true, textVoice: true, dragDrop: false, translationMatch: true }}
                    savedState={initialSavedState}
                    onStateChange={handleStateChange}
                />
            ) : (
                <div>No video lesson ID provided</div>
            )}
        </div>
    );
};

export default QuestionCardExample;